import { useAuth } from "@/context/auth-context";
import { useApi } from "@/hooks/useApi";
import { useToast } from "@/hooks/useToast";
import { getNotifications } from "@/lib/api/user/notifications";
import Link from "next/link";
import { useRef, useState } from "react";
import { FaUserCircle } from "react-icons/fa";
import { GoBell } from "react-icons/go";
import { IoMdExit } from "react-icons/io";
import { LuSearch } from "react-icons/lu";
import { RxHamburgerMenu } from "react-icons/rx";
import useSWR from "swr";
import SearchModal from "../search-modal/search-modal";

const HeaderButtons = ({
	toggleDrawer,
	setAuthenticationModal,
}: {
	toggleDrawer: any;
	setAuthenticationModal: any;
}) => {
	const { user, logout } = useAuth();
	const [searchModal, setSearchModal] = useState(false);
	const { data: notifications, isLoading: isNotificationsLoading } = useSWR(
		user ? ["notifications", user.username] : null,
		() => getNotifications(1, 4),
	);
	const { call } = useApi();
	const { addToast } = useToast();
	const [openNotifications, setOpenNotifications] = useState(false);
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const unreadCount =
		notifications?.content?.filter((n: any) => !n.read).length ?? 0;

	const handleRead = async (id: string) => {
		if (!user) {
			addToast("ابتدا وارد شوید", "info");
			return;
		}

		await call(
			`/api/user/notifications/${id}`,
			{
				method: "PUT",
				headers: { "Content-Type": "application/json" },
			},
			true,
		);
	};

	return (
		<>
			<div className="flex items-center gap-2 md:gap-4">
				{user ? (
					<div className="relative inline-block group">
						<button className="flex bg-light-lime/25 text-white md:px-5 md:py-3 py-3 px-2 rounded-lg text-sm items-center gap-2">
							<FaUserCircle size={22} />
							<span className="mt-1.5 hidden md:block">{user?.username}</span>
						</button>

						<div
							className="absolute right-0 top-full mt-0 w-max bg-light-lime/25 text-white rounded-lg shadow-lg py-2 z-50
										opacity-0 invisible group-hover:opacity-100 group-hover:visible
										pointer-events-none group-hover:pointer-events-auto transition-opacity duration-150"
						>
							<Link
								href={"/notifications"}
								className="flex md:hidden justify-center items-center w-max text-right md:px-4 md:py-2 p-2 text-white hover:bg-gray-700 gap-2"
							>
								<GoBell />
								اعلان ها
							</Link>
							<button
								onClick={logout}
								className="flex justify-center items-center w-full text-right md:px-4 md:py-2 p-2 text-white hover:bg-gray-700 gap-2"
							>
								<IoMdExit />
								<span>خروج</span>
							</button>
						</div>
					</div>
				) : (
					<button
						className="bg-light-lime/25 text-white md:px-5 md:py-3 py-3 px-2 rounded-lg text-sm flex gap-2 hover:opacity-70"
						onClick={() => setAuthenticationModal(true)}
					>
						<FaUserCircle size={22} />
						<span className={"hidden md:block h-5"}>ورود | ثبت نام</span>
					</button>
				)}
				<button
					className="py-3 px-2 md:p-3 bg-light-lime rounded-lg hover:opacity-70"
					onClick={() => setSearchModal(true)}
				>
					<LuSearch className="w-5 h-5 text-black" />
				</button>
				<div className="hidden md:block relative" ref={dropdownRef}>
					<button
						onClick={() => {
							if (user) {
								setOpenNotifications((s) => !s);
							} else {
								addToast("ابتدا باید وارد حساب کاربری خود شوید", "error");
							}
						}}
						aria-expanded={openNotifications}
						aria-haspopup="true"
						className="inline-flex py-3 px-2 md:p-3 bg-dark-grey rounded-lg hover:opacity-70 relative"
					>
						<GoBell className="w-5 h-5 text-white" />
						{unreadCount > 0 && (
							<span
								aria-hidden
								className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full ring-1 ring-dark-grey"
							/>
						)}
					</button>

					{openNotifications && (
						<div
							className="absolute left-0 mt-2 w-80 bg-gray-900 text-white rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto border border-gray-800"
							role="menu"
							aria-label="Notifications"
						>
							<div className="px-4 py-2 border-b border-gray-700 flex items-center justify-between">
								<span className="text-sm font-medium">اعلان‌ها</span>
								<span className="text-xs text-gray-400">
									{unreadCount} خوانده نشده
								</span>
								<Link
									href={"/notifications"}
									className="text-xs text-gray-200 bg-light-blue/25 px-2 py-1 underline rounded-md hover:opacity-70"
								>
									همه اعلان ها
								</Link>
							</div>

							{isNotificationsLoading ? (
								<div className="px-4 py-3 text-sm text-gray-400">
									در حال بارگذاری...
								</div>
							) : (
								<>
									{notifications?.content?.length ? (
										<ul>
											{notifications.content.map((notification: any) => {
												const isRead = Boolean(notification.read);
												return (
													<li
														key={notification.id}
														className={`px-4 py-3 border-b border-gray-700 last:border-0 cursor-pointer ${
															!isRead ? "bg-gray-800" : "transparent"
														}`}
													>
														<div
															className={`flex flex-col`}
															onClick={() => {
																!isRead ? handleRead(notification.id) : null;
															}}
														>
															<span
																className={`text-sm ${
																	isRead
																		? "text-gray-400 font-normal"
																		: "text-white font-semibold"
																}`}
															>
																{notification.title}
															</span>
															{notification.content && (
																<span className="text-xs mt-1 text-gray-300 line-clamp-2">
																	{notification.content}
																</span>
															)}
														</div>
													</li>
												);
											})}
										</ul>
									) : (
										<div className="px-4 py-4 text-center text-sm text-gray-400">
											اعلانی وجود ندارد
										</div>
									)}
								</>
							)}
						</div>
					)}
				</div>
				<button
					className="lg:hidden block py-3 px-2 md:p-3 bg-dark-grey rounded-lg hover:opacity-70 relative"
					onClick={toggleDrawer}
				>
					<RxHamburgerMenu size={20} />
				</button>
			</div>
			{searchModal && <SearchModal onClose={() => setSearchModal(false)} />}
		</>
	);
};

export default HeaderButtons;
